/**
 * ChartScope.jsx
 * Narrowing the performance chart to one brand, one platform, or both.
 *
 * The chart's series arrives already split by brand and platform, so
 * narrowing it is a sum in the browser rather than another request. With
 * everything selected the server's own combined series is used untouched, so
 * the default view can never drift from what it showed before this existed.
 *
 * Dependencies: helpers.js
 */

import { PLATFORMS } from './helpers.js';

/**
 * The series and totals for the chosen scope.
 *
 * @param {object} performance  the dashboard payload's `performance`
 * @param {string} brandId      'all' or a brand id
 * @param {string} platform     'all', 'facebook' or 'instagram'
 */
export function scoped(performance, brandId, platform) {
  const base = performance?.series ?? [];
  const current = performance?.current ?? {};

  if (brandId === 'all' && platform === 'all') return { series: base, current };

  const match = (r) =>
    (brandId === 'all' || r.brandId === brandId) && (platform === 'all' || r.platform === platform);

  // Keep every day of the full window on the axis, so switching scope does
  // not make the chart jump — a quiet day for one brand is a zero, not a gap.
  const byDay = new Map(base.map((d) => [d.day, { day: d.day, impressions: 0, engagements: 0 }]));
  for (const r of performance?.seriesBy ?? []) {
    if (!match(r)) continue;
    const d = byDay.get(r.day) ?? { day: r.day, impressions: 0, engagements: 0 };
    d.impressions += r.impressions;
    d.engagements += r.engagements;
    byDay.set(r.day, d);
  }
  const series = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));

  const rows = (performance?.breakdown ?? []).filter(match);
  const sum = (k) => {
    const vals = rows.map((r) => r[k]).filter((v) => v !== null && v !== undefined);
    return vals.length ? vals.reduce((n, v) => n + v, 0) : null;
  };
  const impressions = sum('impressions');
  const engagements = sum('engagements');

  return {
    series,
    current: {
      impressions,
      engagements,
      rate: impressions ? (engagements / impressions) * 100 : null,
    },
  };
}

/** "for PeekoType on Instagram" — the chart's subtitle for a scope. */
export function scopeLabel(brands, brandId, platform) {
  const brand = brands.find((b) => b.id === brandId)?.name;
  const where = PLATFORMS[platform]?.label;
  if (!brand && !where) return 'across every connected brand';
  if (brand && where) return `for ${brand} on ${where}`;
  if (brand) return `for ${brand}, Facebook and Instagram together`;
  return `on ${where}, every brand together`;
}

/** The two rows of buttons that choose the scope. */
export default function ChartScope({ brands, brandId, platform, onBrand, onPlatform }) {
  return (
    <div className="chart-scope">
      <div className="segmented segmented--sm" role="group" aria-label="Brand">
        <button
          type="button"
          className={brandId === 'all' ? 'is-active' : ''}
          aria-pressed={brandId === 'all'}
          onClick={() => onBrand('all')}
        >
          All brands
        </button>
        {brands.map((b) => (
          <button
            key={b.id}
            type="button"
            className={brandId === b.id ? 'is-active' : ''}
            aria-pressed={brandId === b.id}
            onClick={() => onBrand(b.id)}
          >
            {b.name}
          </button>
        ))}
      </div>

      <div className="segmented segmented--sm" role="group" aria-label="Platform">
        {[{ key: 'all', label: 'Both platforms' }, ...Object.entries(PLATFORMS).map(([key, p]) => ({ key, label: p.label }))].map(
          (p) => (
            <button
              key={p.key}
              type="button"
              className={platform === p.key ? 'is-active' : ''}
              aria-pressed={platform === p.key}
              onClick={() => onPlatform(p.key)}
            >
              {p.label}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
